import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Zap, 
  Check, 
  Shield,
  Smartphone,
  Wallet,
  ArrowRight,
  Star
} from 'lucide-react';
import { 
  FadeIn, 
  ScaleIn, 
  AnimatedCard,
  ConfettiEffect 
} from '../components/ui/AnimatedCard';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';

export function Payments() {
  const { user } = useAuthStore();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'bkash' | 'nagad' | 'card'>('bkash');

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      nameBn: 'বেসিক',
      price: 99,
      period: 'month',
      features: [
        'Access to all practice exams',
        'Basic analytics',
        '5 video lessons per month',
        'Email support',
      ],
      popular: false,
    },
    {
      id: 'premium',
      name: 'Premium',
      nameBn: 'প্রিমিয়াম',
      price: 199,
      period: 'month',
      features: [
        'Access to all exams (including premium)',
        'Advanced analytics & progress tracking',
        'Unlimited video lessons',
        'Priority support',
        'Downloadable resources',
        'Ad-free experience',
      ],
      popular: true,
    },
    {
      id: 'yearly',
      name: 'Yearly',
      nameBn: 'বার্ষিক',
      price: 999,
      period: 'year',
      features: [
        'Everything in Premium',
        'Save 58% compared to monthly',
        'Exclusive live classes',
        '1-on-1 mentoring session',
        'Certificate of completion',
      ],
      popular: false,
    },
  ];

  const handlePayment = () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    // Mock payment processing
    toast.success('Payment processed successfully!');
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);
  };

  return (
    <div className="space-y-8">
      <ConfettiEffect trigger={showConfetti} />
      
      {/* Header */}
      <FadeIn>
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl mb-4"
          >
            <Star className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-slate-900">Upgrade to Premium</h1>
          <p className="text-slate-500 mt-2 max-w-xl mx-auto">
            Unlock all features and take your learning to the next level
          </p>
        </div>
      </FadeIn>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {plans.map((plan, index) => (
          <FadeIn key={plan.id} delay={index * 0.1}>
            <motion.div
              whileHover={{ y: -8 }}
              className={`relative rounded-2xl p-6 h-full ${
                plan.popular 
                  ? 'bg-gradient-to-br from-primary-600 to-primary-700 text-white shadow-xl shadow-primary-500/30' 
                  : 'bg-white border border-slate-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent-500 text-white text-sm font-bold rounded-full">
                  Most Popular
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <p className={`text-sm ${plan.popular ? 'text-primary-100' : 'text-slate-500'}`}>
                  {plan.nameBn}
                </p>
                <div className="mt-4">
                  <span className="text-4xl font-bold">৳{plan.price}</span>
                  <span className={`text-sm ${plan.popular ? 'text-primary-100' : 'text-slate-500'}`}>
                    /{plan.period}
                  </span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className={`w-5 h-5 flex-shrink-0 ${plan.popular ? 'text-green-300' : 'text-green-500'}`} />
                    <span className={`text-sm ${plan.popular ? 'text-primary-50' : 'text-slate-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => setSelectedPlan(plan.id)}
                className={`w-full py-3 rounded-xl font-semibold transition-all ${
                  selectedPlan === plan.id
                    ? plan.popular 
                      ? 'bg-white text-primary-600' 
                      : 'bg-primary-600 text-white'
                    : plan.popular
                      ? 'bg-primary-500 text-white hover:bg-primary-400'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
              </button>
            </motion.div>
          </FadeIn>
        ))}
      </div>

      {/* Payment Methods */}
      {selectedPlan && (
        <FadeIn>
          <AnimatedCard className="max-w-2xl mx-auto p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Select Payment Method</h3>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { id: 'bkash', name: 'bKash', icon: Smartphone },
                { id: 'nagad', name: 'Nagad', icon: Wallet },
                { id: 'card', name: 'Card', icon: CreditCard },
              ].map((method) => (
                <button
                  key={method.id}
                  onClick={() => setPaymentMethod(method.id as any)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    paymentMethod === method.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 hover:border-primary-300'
                  }`}
                >
                  <method.icon className={`w-8 h-8 ${paymentMethod === method.id ? 'text-primary-600' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${paymentMethod === method.id ? 'text-primary-700' : 'text-slate-600'}`}>
                    {method.name}
                  </span>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {paymentMethod === 'card' ? 'Card Number' : 'Phone Number'}
                </label>
                <input
                  type="text"
                  placeholder={paymentMethod === 'card' ? '1234 5678 9012 3456' : '01XXXXXXXXX'}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                />
              </div>

              {paymentMethod === 'card' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Expiry Date</label>
                    <input
                      type="text"
                      placeholder="MM/YY"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">CVV</label>
                    <input
                      type="text"
                      placeholder="123"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">Your payment is secured with SSL encryption</span>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePayment}
                className="w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-bold rounded-xl hover:shadow-lg transition-shadow"
              >
                <Zap className="w-5 h-5" />
                Pay ৳{plans.find(p => p.id === selectedPlan)?.price}
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </AnimatedCard>
        </FadeIn>
      )}
    </div>
  );
}
